// src/components/SocialButtons.jsx
// 국가별 우선순위로 소셜 로그인 버튼을 렌더. 클릭 시 리다이렉트 인증 시작.
// 로그인/가입 공용 (소셜 인증은 첫 로그인 시 자동 가입됨).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth.js';
import { PROVIDER_META, providerOrder } from '../utils/authProviders.js';
import { detectInAppBrowser, openInExternalBrowser } from '../utils/inAppBrowser.js';

// 인앱 브라우저(WebView)에서 OAuth 가 차단되는 제공자.
// 구글은 disallowed_useragent 로 차단, 애플도 웹뷰에서 불안정 → 외부 브라우저로 유도한다.
// (카카오/라인은 자기 앱 웹뷰에서 정상 작동하므로 제외)
const WEBVIEW_BLOCKED = new Set(['google', 'apple']);

export default function SocialButtons({ country = 'KR', referralCode = null }) {
  const { t } = useTranslation();
  const { socialAuth } = useAuth();
  const [pending, setPending] = useState(null);

  async function go(name) {
    // 인앱 브라우저에서 구글/애플은 OAuth 가 막히므로 현재 페이지를 외부 브라우저로 다시 연다.
    // 외부 브라우저(Chrome/Safari)에서는 구글 로그인이 정상 작동.
    if (WEBVIEW_BLOCKED.has(name) && detectInAppBrowser()) {
      const opened = openInExternalBrowser();
      if (!opened) {
        navigator.clipboard?.writeText(window.location.href).catch(() => {});
        toast(t('auth.inappDesc'), { duration: 6000, icon: '🌐' });
      }
      return;
    }

    setPending(name);
    try {
      await socialAuth(name, { country, referralCode: (referralCode || '').trim() || null });
      // 웹뷰(리다이렉트)면 페이지를 떠나고, 일반 브라우저(팝업)면 여기서 완료 → 페이지가 이동.
    } catch (e) {
      setPending(null);
      // 사용자가 팝업을 닫은 경우는 에러로 보지 않음
      if (e?.code === 'auth/popup-closed-by-user' || e?.code === 'auth/cancelled-popup-request') return;
      toast.error(e?.code || e?.message || 'login failed');
    }
  }

  return (
    <div className="space-y-2">
      {providerOrder(country).map((name, i) => {
        const m = PROVIDER_META[name];
        return (
          <button
            key={name}
            type="button"
            onClick={() => go(name)}
            disabled={!!pending}
            className={`w-full rounded-xl py-3 font-bold text-sm transition active:scale-[0.98] disabled:opacity-60 ${
              i === 0 ? 'shadow-md' : ''
            } ${m.border ? 'border border-gray-300' : ''}`}
            style={{ backgroundColor: m.bg, color: m.fg }}
          >
            {pending === name ? '…' : t(m.i18nKey)}
          </button>
        );
      })}
    </div>
  );
}
